import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";
import {
  FaGlobe,
  FaEnvelope,
  FaPhone,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaLinkedin,
  FaYoutube,
  FaFacebook,
  FaInstagram,
  FaTiktok,
  FaTwitter,
  FaSnapchat,
  FaGooglePlay,
  FaAppStore
} from "react-icons/fa";
import { FiArrowLeft } from "react-icons/fi";
import nobannerplaceholder from "../../assets/images/nobannerplaceholder.jpg";
import "./EditProfile.css";

/**
 * EditProfile
 * - Responsive UI similar to provided mobile screenshot
 * - Avatar/banner click -> opens file picker -> uploads to supabase -> updates profile
 * - Settings rows are clickable and have icons
 */

export default function EditProfile() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [profession, setProfession] = useState("");
  const [bio, setBio] = useState("");
  const [showStats, setShowStats] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [referralCode, setReferralCode] = useState("");
  
  // Business Information fields
  const [businessInfo, setBusinessInfo] = useState({
    website: { value: "", isActive: false },
    email: { value: "", isActive: false },
    phone: { value: "", isActive: false },
    whatsapp: { value: "", isActive: false },
    location: { value: "", isActive: false }
  });

  // Social Links fields
  const [socialLinks, setSocialLinks] = useState({
    linkedin: { value: "", isActive: false },
    youtube: { value: "", isActive: false },
    facebook: { value: "", isActive: false },
    instagram: { value: "", isActive: false },
    tiktok: { value: "", isActive: false },
    twitter: { value: "", isActive: false },
    snapchat: { value: "", isActive: false },
    google_play: { value: "", isActive: false },
    app_store: { value: "", isActive: false }
  });

  const bannerInputRef = useRef(null);
  const avatarInputRef = useRef(null);

  const userIconSvg = useMemo(
    () =>
      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj4KPHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRIOGE0IDQgMCAwIDAtNCA0djIiLz4KPGNpcmNsZSBjeD0iMTIiIGN5PSI3IiByPSI0Ii8+Cjwvc3ZnPgo8L3N2Zz4K",
    []
  );

  // Place resolver helpers BEFORE hooks that use them to avoid initialization errors
  const resolveAvatarUrl = useCallback((filenameOrUrl, userId) => {
    try {
      if (!filenameOrUrl) {
        console.log("No avatar_url provided, using default icon");
        return userIconSvg;
      }
      
      // If it's already a full URL (http/https) or data URI, return as-is
      if (/^https?:\/\//i.test(filenameOrUrl) || filenameOrUrl.startsWith("data:")) {
        console.log("Avatar is already a full URL:", filenameOrUrl);
        return filenameOrUrl;
      }
      
      // If the filename already includes the full path format, use it as-is
      let path = filenameOrUrl;
      if (!filenameOrUrl.includes(userId)) {
        // Construct the path
        path = `${userId}/${filenameOrUrl}`;
      }
      
      console.log("Resolving avatar path:", path);
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      const publicUrl = data?.publicUrl || userIconSvg;
      console.log("Avatar public URL:", publicUrl);
      return publicUrl;
    } catch (e) {
      console.error("Error resolving avatar URL:", e);
      return userIconSvg;
    }
  }, [userIconSvg]);

  const resolveBannerUrl = useCallback((filenameOrUrl, userId) => {
    try {
      if (!filenameOrUrl) return nobannerplaceholder;
      if (/^https?:\/\//i.test(filenameOrUrl) || filenameOrUrl.startsWith("data:")) return filenameOrUrl;
      const path = `banners/${userId}/${filenameOrUrl}`;
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      return data?.publicUrl || nobannerplaceholder;
    } catch (_) {
      return nobannerplaceholder;
    }
  }, []);

  const loadProfile = useCallback(async () => {
    if (!currentUser?.id) return;
    setPageLoading(true);
    setError("");
    try {
      const { data: profile, error: err } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", currentUser.id)
        .single();

      if (err) throw err;

      console.log("Profile loaded:", profile); // Debug log

      setDisplayName(profile?.display_name || profile?.username || "");
      setUsername(profile?.username || "");
      setProfession(profile?.profession || "");
      setBio(profile?.bio || "");
      // Read both camelCase and snake_case; default to true (ON by default)
      setShowStats(Boolean(
        (profile?.stats && (profile.stats.showStats ?? profile.stats.show_stats)) ?? true
      ));
      
      // Resolve avatar URL
      let resolvedAvatar = userIconSvg;
      if (profile?.avatar_url) {
        resolvedAvatar = resolveAvatarUrl(profile.avatar_url, currentUser.id);
        console.log("Avatar URL resolved:", resolvedAvatar); // Debug log
      }
      setAvatarUrl(resolvedAvatar);
      
      // Resolve banner URL
      let resolvedBanner = nobannerplaceholder;
      if (profile?.banner_url) {
        resolvedBanner = resolveBannerUrl(profile.banner_url, currentUser.id);
        console.log("Banner URL resolved:", resolvedBanner); // Debug log
      }
      setBannerUrl(resolvedBanner);
      
      // Load business info
      const businessData = profile?.business_info || {};
      setBusinessInfo({
        website: businessData.website || { value: "", isActive: false },
        email: businessData.email || { value: "", isActive: false },
        phone: businessData.phone || { value: "", isActive: false },
        whatsapp: businessData.whatsapp || { value: "", isActive: false },
        location: businessData.location || { value: "", isActive: false }
      });
      
      // Load social links
      const socialData = profile?.social_links || {};
      setSocialLinks({
        linkedin: typeof socialData.linkedin === 'object' ? socialData.linkedin : { value: socialData.linkedin || "", isActive: !!socialData.linkedin },
        youtube: typeof socialData.youtube === 'object' ? socialData.youtube : { value: socialData.youtube || "", isActive: !!socialData.youtube },
        facebook: typeof socialData.facebook === 'object' ? socialData.facebook : { value: socialData.facebook || "", isActive: !!socialData.facebook },
        instagram: typeof socialData.instagram === 'object' ? socialData.instagram : { value: socialData.instagram || "", isActive: !!socialData.instagram },
        tiktok: typeof socialData.tiktok === 'object' ? socialData.tiktok : { value: socialData.tiktok || "", isActive: !!socialData.tiktok },
        twitter: typeof socialData.twitter === 'object' ? socialData.twitter : { value: socialData.twitter || "", isActive: !!socialData.twitter },
        snapchat: typeof socialData.snapchat === 'object' ? socialData.snapchat : { value: socialData.snapchat || "", isActive: !!socialData.snapchat },
        google_play: typeof socialData.google_play === 'object' ? socialData.google_play : { value: socialData.google_play || "", isActive: !!socialData.google_play },
        app_store: typeof socialData.app_store === 'object' ? socialData.app_store : { value: socialData.app_store || "", isActive: !!socialData.app_store }
      });

      // Load referral code from user_referral_codes table
      const { data: existingReferralCode } = await supabase
        .from('user_referral_codes')
        .select('code')
        .eq('user_id', currentUser.id)
        .maybeSingle();
      
      if (existingReferralCode?.code) {
        setReferralCode(existingReferralCode.code);
      }
    } catch (e) {
      console.error("Error loading profile:", e); // Debug log
      setError(e?.message || "Failed to load profile");
    } finally {
      setPageLoading(false);
    }
  }, [currentUser, userIconSvg, resolveAvatarUrl, resolveBannerUrl]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  

  /* ----------------- Save basic fields ----------------- */
  const handleSave = async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    setError("");
    try {
      const { data: existing } = await supabase
        .from("profiles")
        .select("stats")
        .eq("user_id", currentUser.id)
        .single();

      // If stats are turned off, overwrite to exactly { showStats: false }
      // Otherwise, preserve existing stats and set the flags true
      const nextStats = !showStats
        ? { showStats: false }
        : { ...(existing?.stats || {}), showStats: true };

      // Format social links: only include active ones with values
      const formattedSocialLinks = {};
      Object.keys(socialLinks).forEach(key => {
        if (socialLinks[key].isActive && socialLinks[key].value) {
          formattedSocialLinks[key] = socialLinks[key].value;
        }
      });

      const { error: updateErr } = await supabase
        .from("profiles")
        .update({ 
          display_name: displayName, 
          profession,
          bio, 
          stats: nextStats,
          business_info: businessInfo,
          social_links: formattedSocialLinks
        })
        .eq("user_id", currentUser.id);

      if (updateErr) throw updateErr;

      // Handle referral code - create or update record in user_referral_codes if provided
      if (referralCode && referralCode.trim()) {
        // Get referral validity hours from app_settings (default 3 hours)
        const { data: appSettings } = await supabase
          .from('app_settings')
          .select('referral_link_validity_hours, referral_participants_required')
          .maybeSingle();

        const validityHours = appSettings?.referral_link_validity_hours || 3.0;
        const participantsRequired = appSettings?.referral_participants_required || 3;
        const expiresAt = new Date();
        expiresAt.setHours(expiresAt.getHours() + validityHours);

        // Gold reward ID from user_referral_rewards table
        const goldRewardId = 'f658207f-6682-4f76-acb3-29a809ac7292';

        // Check if user already has a referral code
        const { data: existingCode } = await supabase
          .from('user_referral_codes')
          .select('id')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (existingCode) {
          // Update existing referral code with new code and reward_id
          const { error: updateError } = await supabase
            .from('user_referral_codes')
            .update({
              code: referralCode.trim(),
              reward_id: goldRewardId,
              participants_required: participantsRequired,
              expires_at: expiresAt.toISOString(),
            })
            .eq('user_id', currentUser.id);

          if (updateError) {
            console.error('[EditProfile] referral code update error:', updateError);
            // Don't throw - referral code is optional, continue
          }
        } else {
          // Create new referral code record with reward_id
          const { error: referralError } = await supabase
            .from('user_referral_codes')
            .insert({
              user_id: currentUser.id,
              code: referralCode.trim(),
              reward_id: goldRewardId,
              participants_required: participantsRequired,
              current_participants_count: 0,
              status: 'active',
              expires_at: expiresAt.toISOString(),
            });

          if (referralError) {
            console.error('[EditProfile] referral code creation error:', referralError);
            // Don't throw - referral code is optional, continue
          }
        }
      }

      navigate(-1);
    } catch (e) {
      setError(e?.message || "Failed to save changes");
    } finally {
      setLoading(false);
    }
  };

  /* ----------------- Upload helpers ----------------- */
  const uploadFile = async (file, type) => {
    // type: "avatar" | "banner"
    if (!currentUser?.id || !file) return null;

    setLoading(true);
    setError("");

    const userId = currentUser.id;

    try {
      // First, get current profile to find old file to delete
      const { data: currentProfile, error: profileFetchError } = await supabase
        .from("profiles")
        .select(type === "avatar" ? "avatar_url" : "banner_url")
        .eq("user_id", userId)
        .single();

      if (profileFetchError && profileFetchError.code !== 'PGRST116') {
        console.error("[EditProfile] Error fetching current profile:", profileFetchError);
        // Continue anyway - might be first upload
      }

      // Delete old file if it exists
      const oldFilename = type === "avatar" 
        ? currentProfile?.avatar_url 
        : currentProfile?.banner_url;
      
      if (oldFilename) {
        const oldFolder = type === "avatar" ? `${userId}` : `banners/${userId}`;
        const oldPath = `${oldFolder}/${oldFilename}`;
        
        // Try to delete old file (don't fail if it doesn't exist)
        const { error: deleteError } = await supabase.storage
          .from("avatars")
          .remove([oldPath]);
        
        if (deleteError) {
          console.warn("[EditProfile] Could not delete old file (may not exist):", deleteError);
          // Continue anyway - old file might already be deleted
        } else {
          console.log("[EditProfile] Deleted old file:", oldPath);
        }
      }

      // Match existing convention: avatar_<timestamp>.jpg / banner_<timestamp>.jpg
      const timestamp = Date.now();
      const filename =
        type === "avatar" ? `avatar_${timestamp}.jpg` : `banner_${timestamp}.jpg`;

      // Storage paths used elsewhere in the app
      const folder = type === "avatar" ? `${userId}` : `banners/${userId}`;
      const fullPath = `${folder}/${filename}`;

      // Convert image to JPEG format using canvas
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      const result = await new Promise((resolve, reject) => {
        img.onload = async () => {
          try {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            canvas.toBlob(
              async (blob) => {
                if (!blob) {
                  const err = new Error("Failed to convert image to blob");
                  console.error("[EditProfile] blob error:", err);
                  setError(err.message);
                  setLoading(false);
                  reject(err);
                  return;
                }

                try {
                  // Upload to the same 'avatars' bucket used in SetupAccount
                  const { error: uploadError } = await supabase.storage
                    .from("avatars")
                    .upload(fullPath, blob, {
                      upsert: true,
                      contentType: "image/jpeg",
                    });

                  if (uploadError) {
                    console.error("[EditProfile] upload error:", uploadError);
                    setError(
                      uploadError.message || 
                      "Upload failed. Please check your storage bucket permissions (RLS policies)."
                    );
                    setLoading(false);
                    reject(uploadError);
                    return;
                  }

                  console.log("[EditProfile] File uploaded successfully:", fullPath);

                  const simpleName = filename;

                  // Update profile row with just the filename
                  const updateObj = {};
                  if (type === "avatar") updateObj.avatar_url = simpleName;
                  if (type === "banner") updateObj.banner_url = simpleName;

                  const { error: updErr } = await supabase
                    .from("profiles")
                    .update(updateObj)
                    .eq("user_id", userId);

                  if (updErr) {
                    console.error("[EditProfile] profile update error:", updErr);
                    setError(updErr.message || "Failed to update profile");
                    setLoading(false);
                    reject(updErr);
                    return;
                  }

                  console.log("[EditProfile] Profile updated with new", type, ":", simpleName);

                  // Resolve public URL to show the new image immediately
                  const publicPath = `${folder}/${filename}`;
                  const { data } = supabase.storage
                    .from("avatars")
                    .getPublicUrl(publicPath);
                  const publicUrl = data?.publicUrl || "";

                  if (type === "avatar") {
                    setAvatarUrl(
                      publicUrl || resolveAvatarUrl(simpleName, userId)
                    );
                  } else {
                    setBannerUrl(
                      publicUrl || resolveBannerUrl(simpleName, userId)
                    );
                  }

                  setLoading(false);
                  resolve(simpleName);
                } catch (err) {
                  console.error("[EditProfile] uploadFile error:", err);
                  setError(err.message || "Upload failed");
                  setLoading(false);
                  reject(err);
                }
              },
              "image/jpeg",
              0.9
            );
          } catch (err) {
            console.error("[EditProfile] canvas processing error:", err);
            setError(err.message || "Failed to process image");
            setLoading(false);
            reject(err);
          }
        };

        img.onerror = () => {
          const err = new Error("Failed to load image");
          console.error("[EditProfile] image load error:", err);
          setError(err.message);
          setLoading(false);
          reject(err);
        };

        img.src = URL.createObjectURL(file);
      });

      return result;
    } catch (e) {
      console.error("[EditProfile] uploadFile outer error:", e);
      setError(e?.message || "Upload failed");
      setLoading(false);
      return null;
    }
  };

  /* ----------------- click handlers for inputs ----------------- */
  const onBannerClick = () => {
    if (bannerInputRef.current) bannerInputRef.current.click();
  };
  const onAvatarClick = () => {
    if (avatarInputRef.current) avatarInputRef.current.click();
  };

  const onBannerChange = async (e) => {
    const file = e.target.files?.[0];
    // Reset input immediately to prevent file explorer from reopening
    e.target.value = "";
    
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    try {
      await uploadFile(file, "banner");
    } catch (error) {
      console.error("[EditProfile] Banner upload error:", error);
      // Error message is already set in uploadFile
    }
  };

  const onAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    // Reset input immediately to prevent file explorer from reopening
    e.target.value = "";
    
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    try {
      await uploadFile(file, "avatar");
    } catch (error) {
      console.error("[EditProfile] Avatar upload error:", error);
      // Error message is already set in uploadFile
    }
  };

  /* ----------------- business info handlers ----------------- */
  const updateBusinessInfo = (field, value, isActive) => {
    setBusinessInfo(prev => ({
      ...prev,
      [field]: { value, isActive }
    }));
  };

  const toggleBusinessField = (field) => {
    setBusinessInfo(prev => ({
      ...prev,
      [field]: { 
        ...prev[field], 
        isActive: !prev[field].isActive 
      }
    }));
  };

  /* ----------------- social links handlers ----------------- */
  const updateSocialLink = (field, value, isActive) => {
    setSocialLinks(prev => ({
      ...prev,
      [field]: { value, isActive }
    }));
  };

  const toggleSocialLink = (field) => {
    setSocialLinks(prev => ({
      ...prev,
      [field]: { 
        ...prev[field], 
        isActive: !prev[field].isActive 
      }
    }));
  };


  /* ----------------- render ----------------- */
  return (
    <div className="edit-profile-page">
      <button
        className="edit-profile-back-btn"
        onClick={() => navigate(-1)}
        aria-label="Back"
        title="Back"
      >
        <FiArrowLeft />
      </button>
      <div className="edit-header">
        <h1 className="header-title">Edit Profile</h1>
        <button className="edit-save-btn" onClick={handleSave} disabled={loading || pageLoading}>
          Save
        </button>
      </div>

      {error && <div className="ep-error">{error}</div>}

      <div className="banner-section" onClick={onBannerClick} role="button" tabIndex={0}>
        <img src={bannerUrl} alt="Banner" className="edit-banner-image" />
        <div className="banner-hover-hint" aria-hidden>
          <div className="photo-icon" />
          <div className="hint-text">Change banner</div>
        </div>

        <input
          ref={bannerInputRef}
          className="hidden-file-input"
          type="file"
          accept="image/*"
          onChange={onBannerChange}
          style={{ display: "none" }}
        />

        <div
          className="avatar-wrap"
          onClick={(e) => {
            e.stopPropagation();
            onAvatarClick();
          }}
          role="button"
          tabIndex={0}
        >
          <img 
            src={avatarUrl} 
            alt="Avatar" 
            className="avatar-image" 
            onError={(e) => {
              console.error("Avatar image failed to load:", avatarUrl);
              e.target.src = userIconSvg;
            }}
          />
          <div className="avatar-edit-dot">✚</div>
        </div>

        <input
          ref={avatarInputRef}
          className="hidden-file-input"
          type="file"
          accept="image/*"
          onChange={onAvatarChange}
          style={{ display: "none" }}
        />
      </div>

      <div className="form-card">
        <div className="profile-title">iWinto Profile</div>

        <div className="settings-list">
          {/* <div className="setting-item clickable">
            <div className="si-left">
              <div className="si-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="si-text">
                <div className="si-title">Banner Style</div>
                <div className="si-sub">None</div>
              </div>
            </div>
            <div className="si-chevron">›</div>
          </div> */}

          <div className="setting-item">
            <div className="si-left">
              <div className="si-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M3 3v18h18" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 13v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 9v8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 5v12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="si-text">
                <div className="si-title">Show Stats</div>
                <div className="si-sub">Display profile statistics</div>
              </div>
            </div>
            <label className="switch">
              <input
                type="checkbox"
                checked={showStats}
                onChange={(e) => setShowStats(e.target.checked)}
              />
              <span className="slider" />
            </label>
          </div>

          <div className="setting-item clickable" onClick={() => navigate("/customize-stats")}> 
            <div className="si-left">
              <div className="si-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M4 6h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 4v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 14h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M18 12v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M4 18h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6 16v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="si-text">
                <div className="si-title">Customize Stats</div>
                <div className="si-sub">Choose which stats to display</div>
              </div>
            </div>
            <div className="si-chevron">›</div>
          </div>

          <div className="setting-item clickable">
            <div className="si-left">
              <div className="si-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M3 10h18" stroke="currentColor" strokeWidth="1.4"/>
                </svg>
              </div>
              <div className="si-text">
                <div className="si-title">Customize Tabs</div>
                <div className="si-sub">Arrange the order of tabs in your profile</div>
              </div>
            </div>
            <div className="si-chevron">›</div>
          </div>
        </div>
      </div>

      <div className="form-card">
        {/* About You Section */}
        <div className="section">
          <h2 className="section-title">About You</h2>
          
          <div className="form-row">
            <label>Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
            />
          </div>
          
          <div className="form-row">
            <label>Username</label>
            <input
              type="text"
              value={`@${username}`}
              disabled
              className="disabled-input"
            />
          </div>
          
          <div className="form-row">
            <label>Profession</label>
            <input
              type="text"
              value={profession}
              onChange={(e) => setProfession(e.target.value)}
              placeholder="Your profession"
            />
          </div>
          
          <div className="form-row">
            <label>Bio</label>
            <textarea
              rows={3}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell us about yourself..."
            />
          </div>

          <div className="form-row">
            <label>
              Referral Code <span className="setup-optional">(Optional)</span>
            </label>
            <input
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              placeholder="ABC-123"
            />
          </div>
        </div>
      </div>

      <div className="form-card">
        {/* Business Information Section */}
        <div className="section">
          <h2 className="section-title">Business Information</h2>
          
          {/* Website */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#e3f2fd', color: '#1976d2' }}>
                  <FaGlobe size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">Website</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={businessInfo.website.isActive}
                  onChange={() => toggleBusinessField('website')}
                />
                <span className="slider" />
              </label>
            </div>
            {businessInfo.website.isActive && (
              <input
                type="url"
                value={businessInfo.website.value}
                onChange={(e) => updateBusinessInfo('website', e.target.value, true)}
                placeholder="https://yourwebsite.com"
                className="business-input"
              />
            )}
          </div>

          {/* Email */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#fce4ec', color: '#c2185b' }}>
                  <FaEnvelope size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">Email</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={businessInfo.email.isActive}
                  onChange={() => toggleBusinessField('email')}
                />
                <span className="slider" />
              </label>
            </div>
            {businessInfo.email.isActive && (
              <input
                type="email"
                value={businessInfo.email.value}
                onChange={(e) => updateBusinessInfo('email', e.target.value, true)}
                placeholder="your@email.com"
                className="business-input"
              />
            )}
          </div>

          {/* Phone */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#e8f5e9', color: '#388e3c' }}>
                  <FaPhone size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">Phone</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={businessInfo.phone.isActive}
                  onChange={() => toggleBusinessField('phone')}
                />
                <span className="slider" />
              </label>
            </div>
            {businessInfo.phone.isActive && (
              <input
                type="tel"
                value={businessInfo.phone.value}
                onChange={(e) => updateBusinessInfo('phone', e.target.value, true)}
                placeholder="+1 234 567 8900"
                className="business-input"
              />
            )}
          </div>

          {/* WhatsApp */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#e8f5e9', color: '#25d366' }}>
                  <FaWhatsapp size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">WhatsApp</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={businessInfo.whatsapp.isActive}
                  onChange={() => toggleBusinessField('whatsapp')}
                />
                <span className="slider" />
              </label>
            </div>
            {businessInfo.whatsapp.isActive && (
              <input
                type="tel"
                value={businessInfo.whatsapp.value}
                onChange={(e) => updateBusinessInfo('whatsapp', e.target.value, true)}
                placeholder="+1 234 567 8900"
                className="business-input"
              />
            )}
          </div>

          {/* Location */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#fff3e0', color: '#f57c00' }}>
                  <FaMapMarkerAlt size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">Location</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={businessInfo.location.isActive}
                  onChange={() => toggleBusinessField('location')}
                />
                <span className="slider" />
              </label>
            </div>
            {businessInfo.location.isActive && (
              <input
                type="text"
                value={businessInfo.location.value}
                onChange={(e) => updateBusinessInfo('location', e.target.value, true)}
                placeholder="City, Country"
                className="business-input"
              />
            )}
          </div>
        </div>
      </div>

      <div className="form-card">
        {/* Social Links Section */}
        <div className="section">
          <h2 className="section-title">Social Links</h2>
          
          {/* LinkedIn */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#e3f2fd', color: '#0077b5' }}>
                  <FaLinkedin size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">LinkedIn</div>
                  <div className="si-sub" style={{ fontSize: '12px', color: '#6b7280' }}>linkedin.com</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={socialLinks.linkedin.isActive}
                  onChange={() => toggleSocialLink('linkedin')}
                />
                <span className="slider" />
              </label>
            </div>
            {socialLinks.linkedin.isActive && (
              <input
                type="url"
                value={socialLinks.linkedin.value}
                onChange={(e) => updateSocialLink('linkedin', e.target.value, true)}
                placeholder="linkedin.com/in/yourprofile"
                className="business-input"
              />
            )}
          </div>

          {/* YouTube */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#ffebee', color: '#ff0000' }}>
                  <FaYoutube size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">YouTube</div>
                  <div className="si-sub" style={{ fontSize: '12px', color: '#6b7280' }}>youtube.com</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={socialLinks.youtube.isActive}
                  onChange={() => toggleSocialLink('youtube')}
                />
                <span className="slider" />
              </label>
            </div>
            {socialLinks.youtube.isActive && (
              <input
                type="url"
                value={socialLinks.youtube.value}
                onChange={(e) => updateSocialLink('youtube', e.target.value, true)}
                placeholder="youtube.com/@yourchannel"
                className="business-input"
              />
            )}
          </div>

          {/* Facebook */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#e3f2fd', color: '#1877f2' }}>
                  <FaFacebook size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">Facebook</div>
                  <div className="si-sub" style={{ fontSize: '12px', color: '#6b7280' }}>facebook.com</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={socialLinks.facebook.isActive}
                  onChange={() => toggleSocialLink('facebook')}
                />
                <span className="slider" />
              </label>
            </div>
            {socialLinks.facebook.isActive && (
              <input
                type="url"
                value={socialLinks.facebook.value}
                onChange={(e) => updateSocialLink('facebook', e.target.value, true)}
                placeholder="facebook.com/yourprofile"
                className="business-input"
              />
            )}
          </div>

          {/* Instagram */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#f093fb', backgroundImage: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)', color: '#fff' }}>
                  <FaInstagram size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">Instagram</div>
                  <div className="si-sub" style={{ fontSize: '12px', color: '#6b7280' }}>instagram.com</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={socialLinks.instagram.isActive}
                  onChange={() => toggleSocialLink('instagram')}
                />
                <span className="slider" />
              </label>
            </div>
            {socialLinks.instagram.isActive && (
              <input
                type="url"
                value={socialLinks.instagram.value}
                onChange={(e) => updateSocialLink('instagram', e.target.value, true)}
                placeholder="instagram.com/yourprofile"
                className="business-input"
              />
            )}
          </div>

          {/* TikTok */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#000', color: '#fff' }}>
                  <FaTiktok size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">TikTok</div>
                  <div className="si-sub" style={{ fontSize: '12px', color: '#6b7280' }}>tiktok.com</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={socialLinks.tiktok.isActive}
                  onChange={() => toggleSocialLink('tiktok')}
                />
                <span className="slider" />
              </label>
            </div>
            {socialLinks.tiktok.isActive && (
              <input
                type="url"
                value={socialLinks.tiktok.value}
                onChange={(e) => updateSocialLink('tiktok', e.target.value, true)}
                placeholder="tiktok.com/@yourprofile"
                className="business-input"
              />
            )}
          </div>

          {/* X (Twitter) */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#000', color: '#fff' }}>
                  <FaTwitter size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">X (Twitter)</div>
                  <div className="si-sub" style={{ fontSize: '12px', color: '#6b7280' }}>x.com</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={socialLinks.twitter.isActive}
                  onChange={() => toggleSocialLink('twitter')}
                />
                <span className="slider" />
              </label>
            </div>
            {socialLinks.twitter.isActive && (
              <input
                type="url"
                value={socialLinks.twitter.value}
                onChange={(e) => updateSocialLink('twitter', e.target.value, true)}
                placeholder="x.com/yourprofile"
                className="business-input"
              />
            )}
          </div>

          {/* Snapchat */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#fffc00', color: '#000' }}>
                  <FaSnapchat size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">Snapchat</div>
                  <div className="si-sub" style={{ fontSize: '12px', color: '#6b7280' }}>snapchat.com</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={socialLinks.snapchat.isActive}
                  onChange={() => toggleSocialLink('snapchat')}
                />
                <span className="slider" />
              </label>
            </div>
            {socialLinks.snapchat.isActive && (
              <input
                type="text"
                value={socialLinks.snapchat.value}
                onChange={(e) => updateSocialLink('snapchat', e.target.value, true)}
                placeholder="Your Snapchat username"
                className="business-input"
              />
            )}
          </div>

          {/* Google Play */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#e3f2fd', color: '#607d8b' }}>
                  <FaGooglePlay size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">Google Play</div>
                  <div className="si-sub" style={{ fontSize: '12px', color: '#6b7280' }}>com.example.app</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={socialLinks.google_play.isActive}
                  onChange={() => toggleSocialLink('google_play')}
                />
                <span className="slider" />
              </label>
            </div>
            {socialLinks.google_play.isActive && (
              <input
                type="text"
                value={socialLinks.google_play.value}
                onChange={(e) => updateSocialLink('google_play', e.target.value, true)}
                placeholder="com.example.app"
                className="business-input"
              />
            )}
          </div>

          {/* App Store */}
          <div className="business-field">
            <div className="business-field-header">
              <div className="si-left">
                <div className="si-icon" style={{ background: '#000', color: '#fff' }}>
                  <FaAppStore size={18} />
                </div>
                <div className="si-text">
                  <div className="si-title">App Store</div>
                  <div className="si-sub" style={{ fontSize: '12px', color: '#6b7280' }}>1234567890</div>
                </div>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={socialLinks.app_store.isActive}
                  onChange={() => toggleSocialLink('app_store')}
                />
                <span className="slider" />
              </label>
            </div>
            {socialLinks.app_store.isActive && (
              <input
                type="text"
                value={socialLinks.app_store.value}
                onChange={(e) => updateSocialLink('app_store', e.target.value, true)}
                placeholder="1234567890"
                className="business-input"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
